import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { CredsContext, HelpModeContext } from "./Contexts";
import SaveSharpIcon from "@mui/icons-material/SaveSharp";
import OpenInBrowserRoundedIcon from "@mui/icons-material/OpenInBrowserRounded";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Tools from "./JNSTools";
import CustomAlert from "./CustomAlert";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Backdrop from "@mui/material/Backdrop";
import Dialog from "@mui/material/Backdrop";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

function DashboardTextboxComponent(props) {
  const helpMode = useContext(HelpModeContext).mode;
  const creds = useContext(CredsContext).creds;
  const [localWait, setLocalWait] = useState(false);
  const [expandNotes, setExpandNotes] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  let projectObject = JSON.parse(sessionStorage.getItem("projectObject"));
  const textValue = useRef();
  const dialogTextValue = useRef();
  const authString = Tools.getXmlBodyAuthString(creds);
  const urlString = Tools.getInstanceUrl(creds);

  const initialMessage = props.defaultMessage
    ? props.defaultMessage.split(/<br\s*\/?>/i).join("\n")
    : "";

  const handleSave = async () => {
    setLocalWait(true);
    let newsfeedId = projectObject.newsfeedid;
    if (newsfeedId == "") {
      const newsfeedAddString =
        authString + '<Add type="Newsfeed"></Add></request>';
      const addNewsfeedXML = await Tools.fetchPost(
        newsfeedAddString,
        urlString,
        "Newsfeed",
      );
      if (addNewsfeedXML.xml.length > 0) {
        const addNewsfeedArray = Tools.xmlToArray(addNewsfeedXML.xml);
        newsfeedId = addNewsfeedArray[0].id;
        projectObject = { ...projectObject, newsfeedid: newsfeedId };
        sessionStorage.setItem("projectObject", JSON.stringify(projectObject));
        const updateProjectRequestString =
          authString +
          '<Modify type="Project"><Project><id>' +
          projectObject.id +
          "</id><newsfeedid>" +
          newsfeedId +
          "</newsfeedid></Project></Modify></request>";
        Tools.fetchPost(updateProjectRequestString, urlString);
      }
    }

    let nfMessage = textValue.current.value.replace(/\n/gm, "<br>");

    let strongTagCount = 0;
    while (nfMessage.indexOf("**") >= 0) {
      let strongTag = "<strong>";
      if (strongTagCount % 2 == 1) {
        strongTag = "</strong>";
      }
      nfMessage = nfMessage.replace(/\*\*/, strongTag);
      strongTagCount++;
    }
    let tagId;
    switch (projectObject.oada_project_health__c) {
      case "Green":
        tagId = 1;
        break;
      case "Yellow":
      case "Amber":
        tagId = 2;
        break;
      case "Red":
        tagId = 3;
        break;
      default:
        tagId = 0;
        break;
    }

    const addMessageString =
      authString +
      '<Add type="NewsfeedMessage"><NewsfeedMessage><authorid>' +
      creds.id +
      "</authorid><content><![CDATA[" +
      nfMessage +
      "]]></content><tagid>" +
      tagId +
      "</tagid><newsfeedid>" +
      newsfeedId +
      "</newsfeedid><title>PROJECT NOTES</title></NewsfeedMessage></Add></request>";
    Tools.fetchPost(addMessageString, urlString);

    setLocalWait(false);
    setOpenConfirmation(true);
  };

  const handleGenerate = async () => {
    setLocalWait(true);
    const raidRequestString =
      authString +
      '<Read type="Issue" method="equal to" limit="1000" enable_custom="1"><Issue><project_id>' +
      projectObject.id +
      "</project_id></Issue><_Return><date_resolution_expected/><date_resolution_required/><description/><issue_category_id/><issue_stage_id/><issue_status_id/><name/></_Return></Read></request>";
    const taskRequestString =
      authString +
      '<Read type="Projecttask" method="equal to" limit="1000" enable_custom="1"><Projecttask><projectid>' +
      projectObject.id +
      "</projectid></Projecttask><_Return><calculated_finishes/><calculated_starts/><closed/><fnlt_date/><is_a_phase/><name/><percent_complete/><planned_hours/><priority/><starts/></_Return></Read></request>";
    let raidData;
    const rawRaidData = await Tools.fetchPost(
      raidRequestString,
      urlString,
      "Issue",
    );
    if (rawRaidData.xml.length > 0) {
      raidData = Tools.xmlToArray(rawRaidData.xml);
    }
    let taskData;
    const rawTaskData = await Tools.fetchPost(
      taskRequestString,
      urlString,
      "Projecttask",
    );
    if (rawTaskData.xml.length > 0) {
      taskData = Tools.xmlToArray(rawTaskData.xml);
    }
    const projData =
      ":::project:::" +
      sessionStorage.getItem("projectObject") +
      ":::project:::";
    let dataToSend = projData;
    if (raidData) {
      dataToSend +=
        ":::raid items:::" + JSON.stringify(raidData) + ":::raid items:::";
    }
    if (taskData) {
      dataToSend += ":::task:::" + JSON.stringify(taskData) + ":::task:::";
    }
    let query =
      "what is the status of this project in 1000 characters or less.  Also, what are the most important issues and risks, and what tasks are the project team working on?  Do not use ampersands, greater-than signs, or less-than signs.";
    query +=
      "The EAC fields are oada_eac_hours__c and oada_eac_money__c.  The ETC fields are oada_etc_hours__c and oada_etc_money__c.  Do not use the fields eac_hours__c, eac_money__c, etc_hours__c, or etc_money__c.";
    const answer = await Tools.queryChatGPT(query, dataToSend, "solo");
    textValue.current.value = answer[0].content;
    setLocalWait(false);
  };

  const handleExpand = () => {
    setExpandNotes(true);
  };

  const handleClose = () => {
    setExpandNotes(false);
  };

  const handleDialogSave = () => {
    textValue.current.value = dialogTextValue.current.value;
    handleSave();
    setExpandNotes(false);
  };

  return (
    <>
      <CustomAlert
        alertText="Notes Saved to Newsfeed"
        openAlert={openConfirmation}
        setOpenAlert={setOpenConfirmation}
      />
      <Box
        className="dashTableTitleBar"
        height="12%"
        id={"__dashboardProjectHealth"}
        sx={{ cursor: helpMode ? "help" : "default" }}
      >
        <div>
          <AutoFixHighIcon
            id="__dashboardProjectLinksAdd"
            onClick={helpMode ? null : handleGenerate}
            fontSize="small"
            sx={{
              marginLeft: "5px",
              cursor: helpMode ? "help" : "pointer",
            }}
          />
        </div>
        <div>&nbsp;&nbsp;Project Notes</div>
        <div>
          <OpenInBrowserRoundedIcon
            id="__dashboardOpenNotesDialog"
            onClick={helpMode ? null : handleExpand}
            fontSize="small"
            sx={{
              marginRight: "5px",
              paddinTop: "-5px",
              cursor: helpMode ? "help" : "pointer",
            }}
          />

          <SaveSharpIcon
            id="__dashboardProjectLinksAdd"
            onClick={helpMode ? null : handleSave}
            fontSize="small"
            sx={{
              marginRight: "5px",
              cursor: helpMode ? "help" : "pointer",
            }}
          />
        </div>
      </Box>
      <Box
        height="85%"
        sx={{
          overflow: "hidden",
          border: "solid 1px",
          backgroundColor: "white",
        }}
        id={"__dashboardProjectHealth"}
      >
        <div style={{ position: "relative", backgroundColor: "#fff" }}>
          <Backdrop
            sx={{
              color: "#fff",
              position: "absolute",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={localWait}
          >
            <Stack
              sx={{
                width: "100%",
                color: "grey.500",
              }}
              spacing={4}
            >
              <LinearProgress variant="query" color="secondary" />
              <LinearProgress color="success" />
              <LinearProgress variant="query" color="warning" />
              <LinearProgress color="primary" />
              <LinearProgress variant="query" color="secondary" />
              <LinearProgress color="success" />
              <LinearProgress variant="query" color="warning" />
              <LinearProgress color="primary" />
            </Stack>
          </Backdrop>
        </div>
        <textarea
          ref={(notes) => (textValue.current = notes)}
          rows={9}
          defaultValue={initialMessage}
          placeholder="Type the project notes here..."
          style={{
            resize: "none",
            width: "99%",
            display: "inline-block",
            height: "100%",
            fontFamily: "sans-serif",
            fontSize: "small",
            cursor: "text",
            border: "none",
          }}
        ></textarea>
      </Box>
      <Dialog open={expandNotes} onClose={handleClose} sx={{ zIndex: 1000 }}>
        <Box
          sx={{
            backgroundColor: "gainsboro",
            padding: "10px",
            border: "solid 2px",
            width: "1000px",
          }}
        >
          <Typography
            sx={{
              fontSize: "large",
              backgroundColor: "#643f69",
              color: "white",
              borderRadius: "5px",
              paddingLeft: "5px",
              marginBottom: "15px",
            }}
          >
            Project Notes
          </Typography>
          <Box>
            <textarea
              rows={30}
              ref={(notes) => (dialogTextValue.current = notes)}
              defaultValue={textValue.current ? textValue.current.value : null}
              style={{
                resize: "none",
                width: "99%",
                display: "inline-block",
                verticalAlign: "middle",
                fontFamily: "sans-serif",
                fontSize: "medium",
                cursor: "text",
              }}
            ></textarea>
          </Box>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button className="dialogConfirmCancelButton" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="dialogConfirmCancelButton"
              onClick={handleDialogSave}
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

export default DashboardTextboxComponent;

DashboardTextboxComponent.propTypes = {
  defaultMessage: PropTypes.string,
};
